import { useEffect, useState } from 'react';
import { format } from 'date-fns-tz';

import { useAlert } from 'contexts/alert/useAlert';
import {
  MobileIssueDtoArtifactFragment,
  ScheduledContentCancelInput,
  useCancelScheduledContentMutation,
  useGetScheduledContentByTimeWithAllessehIdQuery,
  useScheduleContentMutation
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { FOREVER_UTC, TIMES } from 'utils/time';
import {
  deriveScheduledState,
  ScheduledFromQuery,
  shouldClearSoonPublishUtc,
  shouldSetSoonPublishUtc,
  toScheduledIssueDto
} from './useScheduling.utils';

interface UseIssueSchedulingParams {
  issue: MobileIssueDtoArtifactFragment | null;
}

export function useIssueScheduling({ issue }: UseIssueSchedulingParams) {
  const { alertError, alertSuccess, removeAlert } = useAlert();
  const publicationKey = useConvertedProperty();

  const [hasRecentlyScheduled, setHasRecentlyScheduled] = useState(false);
  const [scheduledIssues, setScheduledIssues] = useState<ScheduledFromQuery[] | null>(null);
  const [soonToBePublishedScheduledContentPublishUtc, setSoonToBePublishedScheduledContentPublishUtc] = useState<
    number | null
  >(null);

  const allessehId = issue?.metadata.allessehId;

  const { mutateAsync: schedule, isLoading: isScheduling } = useScheduleContentMutation();
  const { mutateAsync: cancelSchedule, isLoading: isDeleting } = useCancelScheduledContentMutation();

  const queryArgs = allessehId ? { allessehId, maxTimestamp: FOREVER_UTC } : undefined;

  // @ts-expect-error TS cannot infer that allessehId is defined when enabled is true
  const { data: contentReadyToPublish, refetch } = useGetScheduledContentByTimeWithAllessehIdQuery(queryArgs, {
    enabled: !!allessehId
  });

  const handleSchedule = async (nextPublishUtc: number): Promise<boolean> => {
    removeAlert();

    if (!issue || !allessehId) return false;

    try {
      await schedule({
        publicationKey: publicationKey ?? '',
        scheduledContentCreateInput: toScheduledIssueDto({
          issue,
          publishUtc: nextPublishUtc
        })
      });

      setHasRecentlyScheduled(true);
      alertSuccess(`Scheduled for ${format(nextPublishUtc, 'MMM d, yyyy h:mm aaa z')}.`);

      if (shouldSetSoonPublishUtc(soonToBePublishedScheduledContentPublishUtc, nextPublishUtc, Date.now())) {
        setSoonToBePublishedScheduledContentPublishUtc(nextPublishUtc);
      }

      await refetch();
      return true;
    } catch (e: unknown) {
      alertError(e instanceof Error ? `Could not schedule. ${e.message}` : 'Could not schedule.', {
        wrapNewlines: true
      });
      return false;
    }
  };

  const handleDeleteSchedule = async (deletePublishUtc: number) => {
    removeAlert();

    if (!allessehId || !deletePublishUtc) return;

    const input: ScheduledContentCancelInput = {
      allessehId,
      publishUtc: deletePublishUtc
    };

    try {
      await cancelSchedule({
        publicationKey: publicationKey ?? '',
        scheduledContentCancelInput: input
      });

      setHasRecentlyScheduled(true);
      alertSuccess('Scheduled content deleted.');

      if (shouldClearSoonPublishUtc(soonToBePublishedScheduledContentPublishUtc, deletePublishUtc)) {
        setSoonToBePublishedScheduledContentPublishUtc(null);
      }

      await refetch();
    } catch (e: unknown) {
      alertError(e instanceof Error ? `Could not delete schedule. ${e.message}` : 'Could not delete schedule.', {
        wrapNewlines: true
      });
    }
  };

  useEffect(() => {
    const derived = deriveScheduledState(contentReadyToPublish?.scheduledContentByTimeWithAllessehId);
    setScheduledIssues(derived.scheduledIssues);
    setSoonToBePublishedScheduledContentPublishUtc(derived.soonPublishUtc);
  }, [contentReadyToPublish]);

  useEffect(() => {
    if (!hasRecentlyScheduled) return;
    const t = setTimeout(() => setHasRecentlyScheduled(false), 3000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(t);
  }, [hasRecentlyScheduled]);

  useEffect(() => {
    if (!allessehId) return;

    const publishUtc = soonToBePublishedScheduledContentPublishUtc;
    if (!publishUtc) return;

    const now = Date.now();

    // Start polling shortly before publish time, and continue a bit after.
    const START_BEFORE_MS = 30 * TIMES.ONE_SECOND;
    const STOP_AFTER_MS = 5 * TIMES.ONE_MINUTE;
    const INTERVAL_MS = 5 * TIMES.ONE_SECOND;

    if (now < publishUtc - START_BEFORE_MS) return;

    let stopped = false;
    const stopDeadline = publishUtc + STOP_AFTER_MS;

    const tick = async () => {
      if (stopped) return;

      // Stop if we've passed the deadline
      if (Date.now() > stopDeadline) {
        stopped = true;
        return;
      }

      const res = await refetch();

      const items = res.data?.scheduledContentByTimeWithAllessehId ?? [];
      const stillExists = items.some((i) => i.publishUtc === publishUtc);

      // If it no longer exists, we assume it was published/removed: stop polling.
      if (!stillExists) {
        stopped = true;

        setHasRecentlyScheduled(true);
        alertSuccess('Scheduled item published. Please refresh to see the latest content.');
      }
    };

    // Kick once immediately so you don't wait INTERVAL_MS
    void tick();

    const id = setInterval(() => void tick(), INTERVAL_MS);

    // eslint-disable-next-line consistent-return
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [allessehId, soonToBePublishedScheduledContentPublishUtc, refetch, alertSuccess]);

  return {
    // state
    hasRecentlyScheduled,
    scheduledIssues,
    soonToBePublishedScheduledContentPublishUtc,
    isScheduling,
    isDeleting,

    // handlers
    handleSchedule,
    handleDeleteSchedule
  };
}
